import type {AddressCountryCode} from "@/src/modules/core/types/address-reference";

const CENSUS_BASE_URL =
  import.meta.env.VITE_CENSUS_GEOCODER_BASE_URL ??
  import.meta.env.VITE_CENSUS_GEOCODE_BASE ??
  "/_census/geocoder/locations";
const NOMINATIM_BASE_URL =
  import.meta.env.VITE_NOMINATIM_GEOCODER_BASE_URL ?? "/_nominatim";
const BENCHMARK = "Public_AR_Current";
const DEFAULT_TIMEOUT_MS = 8000;

export type GeocodeInput = {
  number: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  /** Defaults to US (Census). MX uses Nominatim. */
  country?: AddressCountryCode;
};

export type GeocodeResult = {
  lat: number;
  lng: number;
  matchedAddress: string;
};

export class GeocodeServiceError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = "GeocodeServiceError";
  }
}

type CensusResponse = {
  result?: {
    addressMatches?: Array<{
      matchedAddress?: string;
      coordinates?: {
        x?: number;
        y?: number;
      };
    }>;
  };
};

type NominatimSearchResult = {
  lat?: string;
  lon?: string;
  display_name?: string;
};

function normalizeValue(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function normalizeInput(input: GeocodeInput): GeocodeInput {
  return {
    number: normalizeValue(input.number),
    street: normalizeValue(input.street),
    city: normalizeValue(input.city),
    state: normalizeValue(input.state),
    zip: normalizeValue(input.zip),
    country: input.country === "MX" ? "MX" : "US",
  };
}

function parseFirstCensusMatch(response: CensusResponse): GeocodeResult | null {
  const match = response.result?.addressMatches?.[0];
  const latitude = match?.coordinates?.y;
  const longitude = match?.coordinates?.x;
  if (typeof latitude !== "number" || typeof longitude !== "number") return null;
  return {
    lat: latitude,
    lng: longitude,
    matchedAddress: match?.matchedAddress ?? "",
  };
}

function parseFirstNominatimMatch(results: NominatimSearchResult[]): GeocodeResult | null {
  const match = results[0];
  if (!match) return null;
  const latitude = Number.parseFloat(match.lat ?? "");
  const longitude = Number.parseFloat(match.lon ?? "");
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return {
    lat: latitude,
    lng: longitude,
    matchedAddress: match.display_name ?? "",
  };
}

function buildStructuredCensusUrl(input: GeocodeInput): string {
  const params = new URLSearchParams({
    street: `${input.number} ${input.street}`.trim(),
    city: input.city,
    state: input.state.toUpperCase(),
    zip: input.zip,
    benchmark: BENCHMARK,
    format: "json",
  });
  return `${CENSUS_BASE_URL}/address?${params.toString()}`;
}

function buildOnelineCensusUrl(input: GeocodeInput): string {
  const params = new URLSearchParams({
    address: `${input.number} ${input.street}, ${input.city}, ${input.state} ${input.zip}`,
    benchmark: BENCHMARK,
    format: "json",
  });
  return `${CENSUS_BASE_URL}/onelineaddress?${params.toString()}`;
}

function buildNominatimSearchUrl(input: GeocodeInput): string {
  const params = new URLSearchParams({
    street: `${input.number} ${input.street}`.trim(),
    city: input.city,
    state: input.state,
    postalcode: input.zip,
    countrycodes: "mx",
    format: "json",
    limit: "1",
  });
  return `${NOMINATIM_BASE_URL}/search?${params.toString()}`;
}

async function requestJsonWithTimeout<T>(
  url: string,
  timeoutMs: number,
  outerSignal: AbortSignal | undefined,
  serviceLabel: string,
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  const onAbort = () => controller.abort();
  outerSignal?.addEventListener("abort", onAbort);
  try {
    const response = await fetch(url, {signal: controller.signal});
    if (!response.ok) {
      throw new GeocodeServiceError(`${serviceLabel} returned HTTP ${response.status}`);
    }
    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof GeocodeServiceError) throw error;
    const isAbort = error instanceof DOMException && error.name === "AbortError";
    if (isAbort) {
      throw new GeocodeServiceError(`${serviceLabel} request timed out`, error);
    }
    throw new GeocodeServiceError(`${serviceLabel} request failed`, error);
  } finally {
    clearTimeout(timeout);
    outerSignal?.removeEventListener("abort", onAbort);
  }
}

async function geocodeUsAddress(
  input: GeocodeInput,
  options: {signal?: AbortSignal; timeoutMs: number},
): Promise<GeocodeResult | null> {
  const structured = await requestJsonWithTimeout<CensusResponse>(
    buildStructuredCensusUrl(input),
    options.timeoutMs,
    options.signal,
    "Census geocoder",
  );
  const structuredMatch = parseFirstCensusMatch(structured);
  if (structuredMatch) return structuredMatch;

  const oneLine = await requestJsonWithTimeout<CensusResponse>(
    buildOnelineCensusUrl(input),
    options.timeoutMs,
    options.signal,
    "Census geocoder",
  );
  return parseFirstCensusMatch(oneLine);
}

async function geocodeMxAddress(
  input: GeocodeInput,
  options: {signal?: AbortSignal; timeoutMs: number},
): Promise<GeocodeResult | null> {
  const results = await requestJsonWithTimeout<NominatimSearchResult[]>(
    buildNominatimSearchUrl(input),
    options.timeoutMs,
    options.signal,
    "Nominatim geocoder",
  );
  if (!Array.isArray(results)) {
    throw new GeocodeServiceError("Nominatim geocoder returned an unexpected response");
  }
  return parseFirstNominatimMatch(results);
}

export async function geocodeAddress(
  rawInput: GeocodeInput,
  options: {signal?: AbortSignal; timeoutMs?: number} = {},
): Promise<GeocodeResult | null> {
  const input = normalizeInput(rawInput);
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const requestOptions = {signal: options.signal, timeoutMs};

  if (input.country === "MX") {
    return geocodeMxAddress(input, requestOptions);
  }

  return geocodeUsAddress(input, requestOptions);
}
